import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../services/auth.service';

/**
 * Guard theo ROLE dùng chung, đọc danh sách role từ `data.roles` của route.
 *
 * Khác `deadlineConfigGuard` (hardcode 2 role trong guard) và `permissionGuard`
 * (kiểm tra permission thay vì role). Dùng cho các trang thống kê v2 — phân quyền
 * theo ROLE nghiệp vụ, không tạo permission riêng.
 *
 * Guard này chỉ ẩn UI. Phạm vi dữ liệu vẫn do backend `accessWhere()` quyết định:
 * CVTĐ/KSV/TĐV ký/NVKD chỉ thấy hồ sơ mình phụ trách, GIAM_DOC_NV thấy tất cả văn phòng.
 */
export const roleGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.isAuthenticated()) {
    router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
    return false;
  }

  const requiredRoles = route.data?.['roles'] as string[] | undefined;
  if (!requiredRoles?.length) {
    return true;
  }

  const currentUser = await firstValueFrom(authService.currentUser$);
  if (hasAnyRole(requiredRoles, currentUser)) {
    return true;
  }

  router.navigate(['/']);
  return false;
};

function hasAnyRole(requiredRoles: string[], currentUser: any): boolean {
  return (
    matchesStoredRoleArray(requiredRoles, 'role') ||
    matchesStoredRoleObjects(requiredRoles, 'jhi-cn_role') ||
    matchesUserRoleList(requiredRoles, currentUser?.roles) ||
    matchesUserRoleList(requiredRoles, currentUser?.global_roles)
  );
}

/** localStorage 'role': mảng chuỗi role. */
function matchesStoredRoleArray(requiredRoles: string[], key: string): boolean {
  const stored = parseStored(key);
  if (!Array.isArray(stored)) {
    return false;
  }
  return requiredRoles.some(required => stored.includes(required));
}

/** localStorage 'jhi-cn_role': mảng object { role: ... }. */
function matchesStoredRoleObjects(requiredRoles: string[], key: string): boolean {
  const stored = parseStored(key);
  if (!Array.isArray(stored)) {
    return false;
  }
  return requiredRoles.some(required =>
    stored.some((item: any) => item?.role === required)
  );
}

function matchesUserRoleList(requiredRoles: string[], roles: unknown): boolean {
  if (!Array.isArray(roles)) {
    return false;
  }
  return requiredRoles.some(required =>
    roles.some((role: any) => {
      if (typeof role === 'string') {
        return role === required;
      }
      return (role?.role || role?.name || role?.code) === required;
    })
  );
}

function parseStored(key: string): unknown {
  const value = localStorage.getItem(key);
  if (!value) {
    return null;
  }
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}
